'use client';
import { useTranslations } from 'next-intl';
import LocaleLink from '@/components/LocaleLink';
import { useCallback, useState } from 'react';
import CookieManager from '@/utils/cookie';
import AcceptedCookies from '@/types/AcceptedCookies';
import updateConsent from '@/utils/updateConsent';
import dynamic from 'next/dynamic';
import useAnalytics from '@/utils/analytics';

const CookiesFormModal = dynamic(
	() =>
		import('@/components/cookies/CookiesFormModal').then((mod) => mod.default),
	{ ssr: false }
);
const CookieBanner = ({
	locale,
	referrer,
}: {
	locale: string;
	referrer: string;
}) => {
	const t = useTranslations('CookieBanner');
	const [isBannerVisible, setIsBannerVisible] = useState(true);
	const [isFormVisible, setIsFormVisible] = useState(false);
	const analytics = useAnalytics({
		pushPageView: false,
		referrer,
	});
	const handleCookieChoose = useCallback(
		(
			allAccept: boolean,
			allReject: boolean,
			cookieObject: AcceptedCookies | null
		) => {
			let savedObject: AcceptedCookies;
			if (allAccept) {
				savedObject = {
					necessaryCookies: true,
					isAnalyticsCookiesAccepted: true,
					functionalCookies: true,
					personalCookies: true,
					advertiseCookies: true,
				};
			} else if (allReject || !cookieObject) {
				savedObject = {
					necessaryCookies: true,
					isAnalyticsCookiesAccepted: false,
					functionalCookies: false,
					personalCookies: false,
					advertiseCookies: false,
				};
			} else {
				savedObject = cookieObject;
			}
			setIsBannerVisible(false);
			CookieManager.updateCookieConsent(savedObject);
			updateConsent(savedObject);
		},
		[]
	);
	if (!isBannerVisible) {
		return null;
	}

	return (
		<>
			<div className="tablet:translate-y-0 tablet:bottom-0 backdrop-blur-xs fixed bottom-1/2 z-20 w-full translate-y-1/2 bg-black/90">
				<div className="page-container tablet:grid-cols-2 grid w-full items-center gap-4 py-4">
					<p className="text-white">
						{`${t('cookieText')} `}
						<LocaleLink
							href="/cookie-policy/"
							locale={locale}
							target={'_blank'}
							className="text-white underline">
							{t('cookiePolicy')}
						</LocaleLink>
					</p>
					<div className="tablet:grid-cols-3 grid gap-3">
						<button
							className="btn border-1 box-border h-fit w-full rounded-lg border-white py-2"
							onClick={() => {
								analytics?.pushEvent("page:cookieConsentChange", {type :'acceptCookieButton'});
								handleCookieChoose(true, false, null);
							}}>
							{t('accept')}
						</button>
						<button
							className="btn border-1 box-border h-fit w-full rounded-lg border-white py-2"
							onClick={() => {
								analytics?.pushEvent("page:cookieConsentChange", {type :'rejectCookieButton'});
								handleCookieChoose(false, true, null);
							}}>
							{t('reject')}
						</button>
						<button
							className="btn border-1 box-border h-fit w-full rounded-lg border-white py-2"
							onClick={() => {
								analytics?.pushEvent("page:cookieConsentChange", {type :'personalizeCookieButton'});
								setIsFormVisible(true);
							}}>
							{t('personalize')}
						</button>
					</div>
				</div>
			</div>
			<CookiesFormModal
				isBannerVisible={isFormVisible && isBannerVisible}
				locale={locale}
				handleCookieChoose={handleCookieChoose}
				handleClose={() => setIsFormVisible(false)}
			/>
		</>
	);
};
export default CookieBanner;
